import { ethers } from "ethers";
import { CONTRACT_ADDRESS, ESCROW_ABI } from "./config";

// Timeout for tx.wait() in ms (Polygon Amoy can be slow)
const TX_TIMEOUT = 90_000;

export const getContract = (signer: ethers.Signer) => {
  if (!CONTRACT_ADDRESS) {
    throw new Error("Contract address not configured. Please deploy the contract first.");
  }
  return new ethers.Contract(CONTRACT_ADDRESS, ESCROW_ABI, signer);
};

/**
 * Waits for a transaction with a hard timeout fallback.
 * ethers v6 tx.wait(confirms, timeout) can still hang on flaky RPCs,
 * so we race it against a manual timeout that fetches the receipt directly.
 */
async function waitForTx(
  tx: ethers.TransactionResponse,
  timeoutMs: number = TX_TIMEOUT
): Promise<ethers.TransactionReceipt> {
  const receipt = await Promise.race([
    tx.wait(1, timeoutMs),
    new Promise<null>((_, reject) =>
      setTimeout(() => reject(new Error('Transaction confirmation timed out. It may still be processing on-chain.')), timeoutMs + 5000)
    ),
  ]);

  if (!receipt) {
    throw new Error('Transaction was dropped or replaced. Please check your wallet and try again.');
  }

  return receipt;
}

export const contractMethods = {
  createProject: async (
    signer: ethers.Signer,
    provider: string,
    totalAmount: bigint,
    descriptions: string[],
    amounts: bigint[]
  ) => {
    const contract = getContract(signer);
    const tx = await contract.createProject(provider, totalAmount, descriptions, amounts);
    return tx; // Return transaction, not receipt, so we can parse events
  },

  depositFunds: async (signer: ethers.Signer, projectId: number, amount: bigint) => {
    const contract = getContract(signer);
    const tx = await contract.depositFunds(projectId, { value: amount });
    return waitForTx(tx);
  },

  approveMilestone: async (signer: ethers.Signer, projectId: number, milestoneId: number) => {
    const contract = getContract(signer);
    const tx = await contract.approveMilestone(projectId, milestoneId);
    return waitForTx(tx);
  },

  releasePayment: async (signer: ethers.Signer, projectId: number, milestoneId: number) => {
    const contract = getContract(signer);
    const tx = await contract.releasePayment(projectId, milestoneId);
    return waitForTx(tx);
  },

  getProject: async (signer: ethers.Signer, projectId: number) => {
    const contract = getContract(signer);
    return contract.getProject(projectId);
  },

  getMilestones: async (signer: ethers.Signer, projectId: number) => {
    const contract = getContract(signer);
    return contract.getMilestones(projectId);
  },

  openDispute: async (signer: ethers.Signer, projectId: number, milestoneId: number, reason: string) => {
    const contract = getContract(signer);
    const tx = await contract.openDispute(projectId, milestoneId, reason);
    return waitForTx(tx);
  },

  setArbitrator: async (signer: ethers.Signer, projectId: number, arbitratorAddress: string) => {
    const contract = getContract(signer);
    const tx = await contract.setArbitrator(projectId, arbitratorAddress);
    return waitForTx(tx);
  },

  resolveDispute: async (
    signer: ethers.Signer,
    projectId: number,
    milestoneId: number,
    clientSharePercent: number,
    providerSharePercent: number
  ) => {
    const contract = getContract(signer);
    const tx = await contract.resolveDispute(projectId, milestoneId, clientSharePercent, providerSharePercent);
    return waitForTx(tx);
  },
};

